import { useEffect, useLayoutEffect, useRef, useState } from "react";
import styles from "./Reveal.module.css";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* If the reveal has not resolved by now, show the content regardless.
   Content must never be able to stay invisible. */
const FAILSAFE_MS = 1200;

/**
 * Reveals children once, as they enter the viewport.
 *
 * Three independent paths set the visible state, so a failure in any
 * one of them cannot leave content stranded at opacity 0:
 *   1. a synchronous rect check before paint (covers already-in-view
 *      content, and background tabs where IntersectionObserver never
 *      fires because the document is not being composited)
 *   2. IntersectionObserver for content scrolled to later
 *   3. a failsafe timer as a last resort
 */
export default function Reveal({
  children,
  delay = 0,
  as: Tag = "div",
  className = "",
  style,
  ...rest
}) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  // 1 — before paint: reduced motion, or already within the viewport
  useLayoutEffect(() => {
    if (prefersReducedMotion() || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }

    const node = ref.current;
    if (!node) return;

    const rect = node.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    if (rect.top < vh && rect.bottom > 0) setShown(true);
  }, []);

  // 2 + 3 — observer for later content, plus the failsafe
  useEffect(() => {
    if (shown) return;

    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.05 }
    );

    observer.observe(node);
    const failsafe = setTimeout(() => setShown(true), FAILSAFE_MS);

    return () => {
      observer.disconnect();
      clearTimeout(failsafe);
    };
  }, [shown]);

  return (
    <Tag
      ref={ref}
      className={`${styles.reveal} ${shown ? styles.shown : ""} ${className}`}
      style={{ transitionDelay: shown ? `${delay}ms` : "0ms", ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
